import {
  createContext,
  forwardRef,
  type HTMLAttributes,
  type PropsWithChildren,
  type ReactNode,
  type TableHTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
  useContext,
} from 'react';
import {
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/20/solid';

export type OtsDataTableDensity = 'default' | 'compact';
export type OtsDataTableAlign = 'left' | 'right' | 'center';

export type OtsDataTableSectionProps = PropsWithChildren<{
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}>;

/** Consistent heading and action shell for a table inside a larger view. */
export const OtsDataTableSection = ({
  title,
  description,
  actions,
  className,
  children,
}: OtsDataTableSectionProps) => (
  <section className={className}>
    <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 className="text-sm font-semibold text-[var(--ots-secondary)]">
          {title}
        </h3>
        {description != null && (
          <p className="mt-1 text-xs text-[var(--ots-text-muted)]">
            {description}
          </p>
        )}
      </div>
      {actions}
    </div>
    {children}
  </section>
);

const DensityContext = createContext<OtsDataTableDensity>('default');

/**
 * True inside a scrolling table body (OtsTablePanel). The panel owns scrolling and the border, so
 * the table drops its own wrapper overflow (which would break the sticky header) and outline.
 */
export const OtsDataTableScrollContext = createContext(false);

/** Density of the enclosing OtsDataTable, for cell parts that size themselves to match. */
export const useOtsDataTableDensity = () => useContext(DensityContext);
const alignmentClasses: Record<OtsDataTableAlign, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};
const cellDensityClasses: Record<OtsDataTableDensity, string> = {
  default: 'px-4 py-3',
  compact: 'px-3 py-2.5',
};

export type OtsDataTableProps = PropsWithChildren<{
  density?: OtsDataTableDensity;
  /** Includes the standard rounded border. Disable when the table sits inside another bordered surface. */
  contained?: boolean;
  className?: string;
  tableClassName?: string;
}> &
  Omit<TableHTMLAttributes<HTMLTableElement>, 'className'>;

/** Shared responsive table surface used by allocation, cost, and other compact operational ledgers. */
export const OtsDataTable = ({
  density = 'default',
  contained = true,
  className,
  tableClassName,
  children,
  ...tableProps
}: OtsDataTableProps) => {
  const inScrollPanel = useContext(OtsDataTableScrollContext);
  return (
    <DensityContext.Provider value={density}>
      <div
        className={[
          inScrollPanel ? 'min-w-0' : 'min-w-0 max-w-full overflow-x-auto',
          contained && !inScrollPanel
            ? 'rounded-xl border border-[var(--ots-outline-variant)]'
            : '',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <table
          {...tableProps}
          className={[
            'min-w-full divide-y divide-[var(--ots-outline-variant)] text-left',
            density === 'compact' ? 'text-xs' : 'text-sm',
            tableClassName,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {children}
        </table>
      </div>
    </DensityContext.Provider>
  );
};

export const OtsDataTableHead = ({
  className,
  ...props
}: HTMLAttributes<HTMLTableSectionElement>) => {
  const inScrollPanel = useContext(OtsDataTableScrollContext);
  return (
    <thead
      {...props}
      className={[
        'bg-[var(--ots-surface-variant)] text-xs uppercase tracking-wide text-[var(--ots-text-muted)]',
        inScrollPanel ? 'sticky top-0 z-10' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    />
  );
};

export const OtsDataTableBody = ({
  className,
  ...props
}: HTMLAttributes<HTMLTableSectionElement>) => (
  <tbody
    {...props}
    className={[
      'divide-y divide-[var(--ots-outline-variant)] bg-[var(--ots-surface)] text-[var(--ots-text)]',
      className,
    ]
      .filter(Boolean)
      .join(' ')}
  />
);

export const OtsDataTableRow = ({
  className,
  ...props
}: HTMLAttributes<HTMLTableRowElement>) => (
  <tr
    {...props}
    className={['transition-colors hover:bg-[var(--ots-background)]', className]
      .filter(Boolean)
      .join(' ')}
  />
);

export type OtsDataTableHeaderCellProps =
  ThHTMLAttributes<HTMLTableCellElement> & { align?: OtsDataTableAlign };
export const OtsDataTableHeaderCell = ({
  align = 'left',
  className,
  ...props
}: OtsDataTableHeaderCellProps) => {
  const density = useContext(DensityContext);
  return (
    <th
      {...props}
      className={[
        cellDensityClasses[density],
        alignmentClasses[align],
        'font-semibold',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    />
  );
};

export type OtsDataTableSortDirection = 'asc' | 'desc';
export type OtsDataTableSortableHeaderCellProps =
  OtsDataTableHeaderCellProps & {
    direction?: OtsDataTableSortDirection;
    onSort: () => void;
  };

/** Header cell with an accessible ascending/descending sort control. */
export const OtsDataTableSortableHeaderCell = ({
  direction,
  onSort,
  align = 'left',
  className,
  children,
  ...props
}: OtsDataTableSortableHeaderCellProps) => (
  <OtsDataTableHeaderCell
    {...props}
    align={align}
    aria-sort={
      direction ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'
    }
    className={['p-0', className].filter(Boolean).join(' ')}
  >
    <button
      type="button"
      onClick={onSort}
      className={[
        'flex w-full items-center gap-1.5 rounded px-3 py-2.5 font-semibold hover:bg-[var(--ots-primary-container)] hover:text-[var(--ots-primary)] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--ots-primary)]',
        align === 'right'
          ? 'justify-end'
          : align === 'center'
          ? 'justify-center'
          : 'justify-start',
      ].join(' ')}
    >
      <span>{children}</span>
      {direction === 'asc' ? (
        <ChevronUpIcon aria-hidden="true" className="size-3.5 shrink-0" />
      ) : direction === 'desc' ? (
        <ChevronDownIcon aria-hidden="true" className="size-3.5 shrink-0" />
      ) : (
        <ChevronUpDownIcon
          aria-hidden="true"
          className="size-3.5 shrink-0 opacity-60"
        />
      )}
    </button>
  </OtsDataTableHeaderCell>
);

export type OtsDataTableFilterInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'className'
> & { className?: string };

/** Compact themed text filter intended for a data-table header row. */
export const OtsDataTableFilterInput = forwardRef<
  HTMLInputElement,
  OtsDataTableFilterInputProps
>(function OtsDataTableFilterInput({ className, ...props }, ref) {
  return (
    <input
      {...props}
      ref={ref}
      className={[
        'min-h-8 w-full min-w-24 rounded-md border border-[var(--ots-field-outline)] bg-[var(--ots-field)] px-2 text-xs font-normal normal-case tracking-normal text-[var(--ots-text)] placeholder:text-[var(--ots-text-muted)]/70 focus:border-[var(--ots-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--ots-primary)]/15',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    />
  );
});

export type OtsDataTableFilterSelectProps = Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  'className'
> & { className?: string };

/** Compact themed select filter intended for a data-table header row. */
export const OtsDataTableFilterSelect = ({
  className,
  ...props
}: OtsDataTableFilterSelectProps) => (
  <select
    {...props}
    className={[
      'min-h-8 w-full min-w-24 rounded-md border border-[var(--ots-field-outline)] bg-[var(--ots-field)] px-2 text-xs font-normal normal-case tracking-normal text-[var(--ots-text)] focus:border-[var(--ots-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--ots-primary)]/15',
      className,
    ]
      .filter(Boolean)
      .join(' ')}
  />
);

export type OtsDataTableCellProps = TdHTMLAttributes<HTMLTableCellElement> & {
  align?: OtsDataTableAlign;
};
export const OtsDataTableCell = ({
  align = 'left',
  className,
  ...props
}: OtsDataTableCellProps) => {
  const density = useContext(DensityContext);
  return (
    <td
      {...props}
      className={[
        cellDensityClasses[density],
        alignmentClasses[align],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    />
  );
};

export type OtsDataTableRowHeaderProps =
  ThHTMLAttributes<HTMLTableCellElement> & { align?: OtsDataTableAlign };
export const OtsDataTableRowHeader = ({
  align = 'left',
  className,
  scope = 'row',
  ...props
}: OtsDataTableRowHeaderProps) => {
  const density = useContext(DensityContext);
  return (
    <th
      {...props}
      scope={scope}
      className={[
        cellDensityClasses[density],
        alignmentClasses[align],
        'font-semibold',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    />
  );
};

export type OtsKeyValueTableItem = {
  id: string;
  label: ReactNode;
  value: ReactNode;
};

export type OtsKeyValueTableProps = {
  items: OtsKeyValueTableItem[];
  ariaLabel: string;
  density?: OtsDataTableDensity;
  contained?: boolean;
  className?: string;
  valueAlign?: OtsDataTableAlign;
};

/** Foundational two-column table for compact labels and values, including popover details. */
export const OtsKeyValueTable = ({
  items,
  ariaLabel,
  density = 'compact',
  contained = false,
  className,
  valueAlign = 'right',
}: OtsKeyValueTableProps) => (
  <OtsDataTable
    density={density}
    contained={contained}
    className={className}
    aria-label={ariaLabel}
  >
    <OtsDataTableBody>
      {items.map((item) => (
        <OtsDataTableRow key={item.id}>
          <OtsDataTableRowHeader>{item.label}</OtsDataTableRowHeader>
          <OtsDataTableCell align={valueAlign}>{item.value}</OtsDataTableCell>
        </OtsDataTableRow>
      ))}
    </OtsDataTableBody>
  </OtsDataTable>
);
